<div class="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.15),_transparent_42%),linear-gradient(180deg,#0b1020_0%,#0f1117_40%,#090c14_100%)] text-slate-100 px-4 py-10">
  <div class="mx-auto w-full max-w-6xl">
    <div class="mb-6 flex justify-center">
      <img
        src="/assets/images/icon/fitmed-icon-coloured.svg"
        alt="FitMed"
        class="h-12 w-auto select-none drop-shadow-[0_8px_24px_rgba(0,0,0,0.35)]" />
    </div>

    <app-card class="block h-full min-h-0 w-full" [rounded]="true">
      <div header class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center gap-2.5">
          <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-500/10 text-sky-400 border border-sky-500/20 shadow-sm">
            <app-icon icon="mdi-lock-outline" size="5"></app-icon>
          </div>
          <h1 class="text-xl md:text-2xl font-semibold tracking-tight text-slate-100">Secure document upload</h1>
        </div>
        @if (!errorType) {
          <p class="mt-1 max-w-2xl text-sm leading-6 text-slate-300">
            Upload documents securely. You can preview and review files before confirming.
          </p>
        }
      </div>

      <div body class="flex h-full min-h-0 flex-col gap-5 p-6 md:p-8">
        @if (errorType) {
          <div class="py-10 px-4 flex flex-col items-center text-center max-w-md mx-auto w-full">
            <div class="flex h-14 w-14 items-center justify-center rounded-2xl mb-4 shadow-sm"
              [class.bg-amber-500\/10]="errorType === 'expired' || errorType === 'revoked'"
              [class.text-amber-400]="errorType === 'expired' || errorType === 'revoked'"
              [class.border]="errorType === 'expired' || errorType === 'revoked'"
              [class.border-amber-500\/20]="errorType === 'expired' || errorType === 'revoked'"
              [class.bg-rose-500\/10]="errorType === 'invalid' || errorType === 'missing' || errorType === 'generic'"
              [class.text-rose-400]="errorType === 'invalid' || errorType === 'missing' || errorType === 'generic'"
              [class.border]="errorType === 'invalid' || errorType === 'missing' || errorType === 'generic'"
              [class.border-rose-500\/20]="errorType === 'invalid' || errorType === 'missing' || errorType === 'generic'">

              @switch (errorType) {
                @case ('expired') {
                  <app-icon icon="mdi-clock-outline" size="7"></app-icon>
                }
                @case ('revoked') {
                  <app-icon icon="mdi-lock-outline" size="7"></app-icon>
                }
                @default {
                  <app-icon icon="mdi-information-outline" size="7"></app-icon>
                }
              }
            </div>

            <h2 class="text-xl font-semibold tracking-tight text-slate-100">
              @switch (errorType) {
                @case ('expired') { Link Expired }
                @case ('revoked') { Link Closed }
                @case ('invalid') { Invalid Link }
                @case ('missing') { Link Required }
                @default { Link Unavailable }
              }
            </h2>

            <p class="mt-2 text-sm leading-relaxed text-slate-300">
              {{ errorDetails }}
            </p>

            @if (rawTokenForResend && errorType !== 'missing') {
              <div class="mt-6 flex flex-col items-center gap-3 w-full">
                <app-button
                  [btnTxt]="cooldownRemainingSeconds > 0 ? ('Resend link in ' + resendCooldownFormatted) : 'Request New Link'"
                  buttonType="secondary"
                  [disabled]="cooldownRemainingSeconds > 0 || requestingLink"
                  [loading$]="requestingLink"
                  (click)="requestNewLink()">
                </app-button>
                @if (resendSuccessMessage) {
                  <div class="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-xs text-emerald-200 text-center w-full">
                    {{ resendSuccessMessage }}
                  </div>
                }
              </div>
            }
          </div>
        } @else {
          <!-- Integrated Secure Upload Link & Confirmation Banner -->
          <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 rounded-2xl border border-sky-500/20 bg-sky-500/5 px-5 py-4 text-xs md:text-sm text-sky-200">
            <div class="flex items-start gap-3">
              <span class="relative flex h-2.5 w-2.5 mt-1 shrink-0">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <div class="space-y-1">
                <div class="font-medium text-slate-100 flex items-center gap-2">
                  <span>Encrypted secure upload link</span>
                  @if (expiryFormatted) {
                    <span class="text-slate-400">•</span>
                    <span class="text-slate-300">Expires <strong class="text-slate-100 font-medium">{{ expiryFormatted }}</strong></span>
                  }
                </div>
                <p class="text-xs text-slate-400 leading-relaxed">
                  For security, confirming these files will permanently close access to this upload link.
                </p>
              </div>
            </div>

            <div class="flex items-center justify-end shrink-0 pt-2 lg:pt-0">
              <app-button
                [btnTxt]="'Confirm Files'"
                buttonType="primary"
                [disabled]="uploadsDisabled"
                (click)="openConfirmModal()">
              </app-button>
            </div>
          </div>

          @if (error) {
            <div class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
              {{ error }}
            </div>
          }

          @if (success) {
            <div class="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
              {{ success }}
            </div>
          }

          <!-- Section 1: Uploaded for review -->
          <div class="space-y-2">
            <h3 class="text-xs font-medium text-slate-400 tracking-wide px-1">Uploaded for review</h3>
            <app-documents
              [currentPath]="''"
              [rootPath]="''"
              [subdirs]="subdirs"
              [docs]="pendingDocs"
              [loadingFiles]="loadingFiles"
              [loadingUpload]="loadingUpload"
              [uploadingFileCount]="uploadsInFlight"
              [fileOnly]="true"
              [allowDelete]="true"
              [uploadsDisabled]="uploadsDisabled"
              (downloadRequested)="onDownload($event)"
              (previewRequested)="onPreview($event)"
              (uploadRequested)="onUpload($event)"
              (renameRequested)="onRenameDocument($event)"
              (deleteRequested)="onDeleteDocument($event)">
            </app-documents>
          </div>

          <!-- Section 2: Reviewed & accepted -->
          @if (approvedDocs.length > 0) {
            <div class="space-y-2 pt-4">
              <h3 class="text-xs font-medium text-emerald-400/90 tracking-wide px-1 flex items-center gap-1.5">
                <app-icon icon="mdi-check-circle-outline" size="3.5"></app-icon>
                <span>Reviewed & accepted</span>
              </h3>
              <app-documents
                [currentPath]="''"
                [rootPath]="''"
                [subdirs]="[]"
                [docs]="approvedDocs"
                [loadingFiles]="false"
                [loadingUpload]="false"
                [uploadingFileCount]="0"
                [fileOnly]="true"
                [allowDelete]="false"
                [uploadsDisabled]="true"
                (downloadRequested)="onDownload($event)"
                (previewRequested)="onPreview($event)">
              </app-documents>
            </div>
          }
        }
      </div>
    </app-card>
  </div>
</div>

@if (confirmModalOpen) {
  <app-modal [maxWidth]="'620px'" [maxHeight]="'70vh'" [closeable]="!confirming">
    <div header>
      <div class="text-lg font-semibold text-slate-100">Confirm files</div>
    </div>

    <div body>
      <div class="space-y-4 p-5 text-sm leading-6 text-slate-200">
        <p>
          Once you confirm these files, this secure link will stop working for future uploads.
        </p>
        <p>
          If you need to upload more files later, you will need to create a new link from the iOS app just like before.
        </p>

        <div class="flex flex-wrap justify-end gap-3 pt-2">
          <app-button
            buttonType="warning"
            [disabled]="confirming"
            (click)="closeConfirmModal()">
            Cancel
          </app-button>
          <app-button
            buttonType="danger"
            [loading$]="confirming"
            [disabled]="confirming"
            (click)="confirmUploads()">
            Confirm Files
          </app-button>
        </div>
      </div>
    </div>
  </app-modal>
}
